/**
 * Parsing and typing for realtime job-lifecycle frames shared by the backend
 * WS channels that run mutations through an async job queue (e.g. BullMQ).
 *
 * A gateway that adopts the async job flow broadcasts
 * `{ command: 'JobActive' | 'JobCompleted' | 'JobFailed', kind: 'job', entity,
 * jobId, name, state, resultId?, error?, code?, retryAfterSeconds?, ts }`
 * whenever a job it tracks changes state. Frames carry no result payload
 * beyond the affected id (`resultId`); consumers refetch over authorized HTTP
 * once a job completes.
 *
 * A job whose work is broken into items can additionally report progress via
 * `JobProgress` frames (SKY-4468): same envelope, but no `state` — just a
 * `progress` object — since it isn't a lifecycle transition.
 */

export type JobLifecycleCommand = 'JobActive' | 'JobCompleted' | 'JobFailed' | 'JobProgress';

export type JobLifecycleState = 'active' | 'completed' | 'failed';

/**
 * Per-item progress for a job that reports it (only `import` today). The
 * gateway pre-validates this before forwarding: processed/total are always
 * non-negative integers with `processed <= total` and `total > 0`, so
 * `processed / total` is always a safe, in-range division. `parseJobMessage`
 * enforces the same contract client-side (rejecting the frame rather than
 * rendering `NaN`/negative percentages) as a defense against a backend
 * regression. Not necessarily monotonic (a retryable job can restart from
 * 0), so consumers must render the latest value rather than clamping to a
 * maximum.
 */
export interface JobProgressPayload {
  processed: number;
  total: number;
  /** Free-form label (e.g. the item just handled) — presentational only, never parse/branch on it. */
  hint?: string;
}

export interface JobMessage {
  command: JobLifecycleCommand;
  kind: 'job';
  /**
   * Deliberately `string`: the backend may start emitting entities this
   * client version doesn't know yet, and parsing must stay
   * forward-compatible — consumers map unknown values to no-ops rather than
   * rejecting the frame. Each channel package documents its known values.
   */
  entity: string;
  /** Correlates the frame with the `jobId` a mutation's `202` response returned. */
  jobId: string;
  /** The operation the job runs, e.g. `create` | `update` | `archive` | `delete` | `import`. */
  name: string;
  /** Absent on `JobProgress` frames — those carry `progress` instead. */
  state?: JobLifecycleState;
  /** Affected record id, when the job produces a single one (absent for bulk jobs like `import`). */
  resultId?: number;
  /** Present only when `state` is `failed`. */
  error?: string;
  /** Human-readable failure description, present when the backend provides one. */
  message?: string;
  /**
   * Stable failure code when known — format is a per-channel convention, not
   * a contract this entity-agnostic type enforces. The telegram-groups
   * channel sends a **fully-qualified i18n key**, not a bare name — e.g.
   * `telegram-group.exceptions.FLOOD_WAIT`, not `FLOOD_WAIT` — and exports the
   * exact strings consumers must compare against (see `FLOOD_WAIT_CODE` and
   * friends in `@skynet/data-trading-groups`). Never compare against a bare
   * literal there: a wrong value fails silently — the branch simply never
   * matches and the UI falls through to its generic failure path. Other
   * assistant channels (bot-commands, marketing-campaigns, ...) may use a
   * different convention — check that channel's own package before assuming
   * this one.
   */
  code?: string;
  /** Present alongside a `FLOOD_WAIT` `code`. */
  retryAfterSeconds?: number;
  /**
   * The id of the admin who triggered this job, present once the backend
   * applies per-user (audience-scoped) WS frame targeting (SKY-4447) —
   * absent from a backend build that still broadcasts every job frame to
   * every authenticated client. See `isMineJobFrame` in the dashboard's
   * `ws-assistant-effects` for how consumers use this to decide whether a
   * frame is theirs without relying solely on their own bookkeeping.
   */
  initiatorId?: number;
  /** Server-side emission time (ISO); used for deduplicating event bursts. */
  ts: string;
  /** Present only on `JobProgress` frames. */
  progress?: JobProgressPayload;
}

const isJobLifecycleState = (value: unknown): value is JobLifecycleState =>
  value === 'active' || value === 'completed' || value === 'failed';

/** A field that, when present at all, must be of the given type — absent is fine (forward-compatible). */
const isOptional = (value: unknown, type: 'number' | 'string'): boolean =>
  value === undefined || typeof value === type;

const isJobProgressPayload = (value: unknown): value is JobProgressPayload =>
  typeof value === 'object' &&
  value !== null &&
  'processed' in value &&
  typeof value.processed === 'number' &&
  Number.isInteger(value.processed) &&
  value.processed >= 0 &&
  'total' in value &&
  typeof value.total === 'number' &&
  Number.isInteger(value.total) &&
  value.total > 0 &&
  value.processed <= value.total &&
  (!('hint' in value) || isOptional(value.hint, 'string'));

const isJobMessage = (value: unknown): value is JobMessage => {
  if (typeof value !== 'object' || value === null) return false;

  return (
    'command' in value &&
    (value.command === 'JobActive' ||
      value.command === 'JobCompleted' ||
      value.command === 'JobFailed' ||
      value.command === 'JobProgress') &&
    'kind' in value &&
    value.kind === 'job' &&
    'entity' in value &&
    typeof value.entity === 'string' &&
    'jobId' in value &&
    typeof value.jobId === 'string' &&
    'name' in value &&
    typeof value.name === 'string' &&
    (!('resultId' in value) || isOptional(value.resultId, 'number')) &&
    (!('error' in value) || isOptional(value.error, 'string')) &&
    (!('message' in value) || isOptional(value.message, 'string')) &&
    (!('code' in value) || isOptional(value.code, 'string')) &&
    (!('retryAfterSeconds' in value) || isOptional(value.retryAfterSeconds, 'number')) &&
    (!('initiatorId' in value) || isOptional(value.initiatorId, 'number')) &&
    (value.command === 'JobProgress'
      ? 'progress' in value && isJobProgressPayload(value.progress)
      : 'state' in value && isJobLifecycleState(value.state))
  );
};

/**
 * Parses a raw WS `onMessage` payload into a {@link JobMessage}, or returns
 * `null` for any other frame (auth acks, `Invalidate` frames, pongs,
 * malformed JSON).
 *
 * A missing or non-string `ts` is normalized to `''` rather than rejecting
 * the frame — deduplication then falls back to the client clock
 * (`Date.parse('')` is `NaN`), so the frame itself is never lost.
 */
export const parseJobMessage = (payload: { message: string }): JobMessage | null => {
  try {
    const json: unknown = JSON.parse(payload.message);

    if (!isJobMessage(json)) return null;

    return typeof json.ts === 'string' ? json : { ...json, ts: '' };
  } catch {
    return null;
  }
};
